import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Typography, Spinner, Badge } from "stories/components";
import { Checkbox } from "stories/forms";

import { httpResolveIsin, httpBindPriceSource } from "services/private_investment_funds";

/**
 * Pick the listing a security's prices are fetched from.
 *
 * The backend hands back `home_index` and `mtf_indexes` alongside the candidates, and both
 * matter here. When 15 production securities were linked by hand, the resolver's own ranking
 * pointed at a Cboe Europe venue or Frankfurt instead of the home exchange in 12 of them, so
 * the home listing is preselected and the Cboe-style venues start folded away — otherwise the
 * easiest choice on screen is usually the wrong one.
 */
const PriceSourceModal = ({ fund, onBound, onCancel }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [mtfIndexes, setMtfIndexes] = useState([]);
  const [homeIndex, setHomeIndex] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showMtf, setShowMtf] = useState(false);
  const [currencyAck, setCurrencyAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const isin = fund?.isin;

  const resolve = useCallback(() => {
    setLoading(true);
    setError(null);
    httpResolveIsin({ isin }).then(res => {
      const list = res?.data?.candidates || [];
      setCandidates(list);
      setMtfIndexes(res?.data?.mtf_indexes || []);
      setHomeIndex(res?.data?.home_index != null ? res.data.home_index : null);
      setSelected(res?.data?.home_index != null ? res.data.home_index : (list.length ? 0 : null));
      setLoading(false);
    }, err => {
      setError(err?.data?.detail || t("price_source_resolve_failed",
        "Kurssilähteitä ei juuri nyt saada haettua. Yritä hetken kuluttua uudelleen."));
      setLoading(false);
    });
  }, [isin, t]);

  useEffect(() => { resolve() }, [resolve]);

  const chosen = selected != null ? candidates[selected] : null;
  const currencyChanges = Boolean(chosen?.currency && chosen.currency !== fund?.currency_label);
  const handTypedRows = fund?.datas_count || 0;
  const canSubmit = Boolean(chosen) && (!currencyChanges || currencyAck) && !saving;

  const handleBind = () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    httpBindPriceSource(fund.id, {
      isin,
      source: chosen.source,
      symbol: chosen.symbol,
      exchange: chosen.exchange,
      confirm_currency_change: currencyChanges,
    }).then(res => {
      setSaving(false);
      onBound(res?.data);
    }, err => {
      setSaving(false);
      setError(err?.data?.detail || t("price_source_bind_failed",
        "Kurssilähteen käyttöönotto epäonnistui."));
    });
  };

  const renderRow = (candidate, index) => (
    <tr key={`${candidate.source}-${candidate.symbol}-${candidate.exchange}-${index}`}
        onClick={() => setSelected(index)}
        style={{ cursor: "pointer" }}
        className={selected === index ? "table-primary" : ""}>
      <td style={{ width: "2.5rem" }}>
        <input
          type="radio"
          name="price_source_candidate"
          checked={selected === index}
          onChange={() => setSelected(index)}
          aria-label={`${candidate.symbol} ${candidate.exchange}`}
        />
      </td>
      <td>{candidate.source === "infront" ? "Infront" : "FMP"}</td>
      <td>{candidate.name}</td>
      <td>{candidate.symbol}</td>
      <td>
        {candidate.exchange}
        {index === homeIndex && (
          <Badge color="info" size="small" className="ml-2">
            {t("price_source_home_exchange", "Kotipörssi")}
          </Badge>
        )}
      </td>
      <td>{candidate.currency}</td>
    </tr>
  );

  const mainIndexes = candidates.map((c, i) => i).filter(i => !mtfIndexes.includes(i));

  return (
    <div>
      <Typography variant="small" className="text-muted d-block mb-3">
        {fund?.name} · {isin}
      </Typography>

      {loading && (
        <div className="d-flex align-items-center">
          <Spinner />
          <Typography className="ml-2">
            {t("price_source_resolving", "Haetaan listauksia Infrontista ja FMP:stä…")}
          </Typography>
        </div>
      )}

      {!loading && error && (
        <div className="mb-3">
          <Typography className="text-danger">{error}</Typography>
          <Button variant="secondary" size="sm" onClick={resolve} className="mt-2">
            {t("Yritä uudelleen")}
          </Button>
        </div>
      )}

      {!loading && !error && candidates.length === 0 && (
        <Typography>
          {t("price_source_no_candidates",
             "ISINille ei löytynyt listauksia. Tarkista ISIN, tai valitse \"Hoidan kurssit itse\".")}
        </Typography>
      )}

      {!loading && candidates.length > 0 && (
        <>
          <table className="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th />
                <th>{t("price_source_col_source", "Lähde")}</th>
                <th>{t("price_source_col_name", "Nimi")}</th>
                <th>{t("price_source_col_symbol", "Tunnus")}</th>
                <th>{t("price_source_col_exchange", "Pörssi")}</th>
                <th>{t("price_source_col_currency", "Valuutta")}</th>
              </tr>
            </thead>
            <tbody>
              {mainIndexes.map(i => renderRow(candidates[i], i))}
            </tbody>
          </table>

          {mtfIndexes.length > 0 && (
            <>
              <button type="button" className="sk-form-create-link mb-2"
                      onClick={() => setShowMtf(!showMtf)}>
                {showMtf
                  ? t("price_source_hide_mtf", "Piilota muut kauppapaikat")
                  : `${t("price_source_show_mtf", "Muut kauppapaikat")} (${mtfIndexes.length})`}
              </button>
              {showMtf && (
                <table className="table table-sm align-middle mb-2">
                  <tbody>{mtfIndexes.map(i => renderRow(candidates[i], i))}</tbody>
                </table>
              )}
            </>
          )}

          {currencyChanges && (
            <div className="alert alert-warning py-2 px-3 mb-2">
              <Typography bold className="d-block">
                {t("price_source_currency_change", "Valuutta vaihtuu")} {fund?.currency_label} → {chosen.currency}
              </Typography>
              <Typography variant="small" className="d-block mb-2">
                {t("price_source_currency_change_info",
                   "Markkinahinta muunnetaan euroiksi päivän kurssilla. Hankintahinnat säilyvät ennallaan, koska ne tulevat transaktioista.")}
              </Typography>
              <Checkbox
                id="price_source_currency_ack"
                name="price_source_currency_ack"
                checked={currencyAck}
                onChange={() => setCurrencyAck(!currencyAck)}
                label={t("Ymmärrän")}
              />
            </div>
          )}

          {handTypedRows > 0 && (
            <Typography variant="small" className="text-muted d-block mb-2">
              {t("price_source_replaces_rows_1", "Käsin kirjatut kurssit")} ({handTypedRows} {t("kpl")}){" "}
              {t("price_source_replaces_rows_2",
                 "korvataan lähteen historialla. Rivejä ei voi jättää paikalleen: haku pysähtyy tuoreimpaan tallennettuun riviin, jolloin historiaa ei haettaisi lainkaan.")}
            </Typography>
          )}
        </>
      )}

      <div className="d-flex justify-content-end mt-3">
        <Button variant="secondary" onClick={onCancel} className="mr-2" disabled={saving}>
          {t("Peruuta")}
        </Button>
        <Button variant="primary" onClick={handleBind} disabled={!canSubmit}>
          {saving ? t("Tallennetaan…") : t("price_source_apply", "Ota käyttöön")}
        </Button>
      </div>
    </div>
  );
};

export default PriceSourceModal;
